import { useEffect, useState } from 'react'
import { FindAddress } from '../findAddress/FindAddress'
import { StepHeader } from '../step-header/StepHeader'
import s from './BoxForm.module.scss'
import { Properties, Property } from 'types/onboarding'
import { H4 } from 'components/heading/Heading'
import { ArrowButton } from 'components/button/ButtonWithIcon'
import { Check } from 'components/form-elements/Check'
import { SelectPackageForOne } from './SelectPackageForOne'
import { Addresses } from 'stores/onboarding/types'
import { Button } from 'components/button/Button'
import { BackButton } from 'components/back-button/BackButton'

interface BoxFormProps {
  userName: string
  addresses: Addresses
  setPropertyDelivered: (propertyDelivered: boolean) => void
  setProperties: (selectedProperties: Properties) => void
  setAddresses: (addresses: Addresses) => void
  onSubmit: () => void
}

export const BoxForm = ({
  userName,
  setPropertyDelivered,
  addresses,
  setAddresses,
  setProperties,
  onSubmit,
}: BoxFormProps) => {
  const [newProperty, setNewProperty] = useState<Property>()
  const [oldProperty, setOldProperty] = useState<Property>()
  const onPropertyDeliveredChange = (checked: boolean) => {
    setPropertyDelivered(checked ? true : false)
  }

  const [steps, setSteps] = useState<
    'SelectNewAddress' | 'SelectPackageForNewAddress' | 'SelectOldAddress'
  >('SelectNewAddress')

  useEffect(() => {
    if (!newProperty) {
      return
    }
    setProperties([newProperty])
    setAddresses({
      ...addresses,
      address: newProperty?.address,
      postalCode: `${newProperty?.postalCode} ${newProperty?.city}`,
      product: newProperty?.product,
    })
  }, [newProperty, oldProperty])

  useEffect(() => {
    if (!oldProperty) {
      return
    }
    setAddresses({
      ...addresses,
      oldAddress: oldProperty?.address,
      oldPostalCode: `${oldProperty?.postalCode} ${oldProperty?.city}`,
    })
  }, [oldProperty])

  const isValidAddresses = () => {
    return (
      addresses.address !== '' &&
      addresses.postalCode !== '' &&
      addresses.oldAddress !== '' &&
      addresses.oldPostalCode !== ''
    )
  }

  const onClickSubmit = () => {
    if (isValidAddresses()) {
      onSubmit()
    }
  }

  return (
    <div className={s.form}>
      <StepHeader
        title={`Hæ ${userName}`}
        text={
          steps === 'SelectNewAddress'
            ? 'Vinsamlegast fylltu út upplýsingarnar hér fyrir neðan. Við skráum þig í viðskipti og komum svo til þín flutningskössunum.'
            : ''
        }
      />
      <div className={s.form__spacing}>
        {steps === 'SelectNewAddress' && (
          <FindAddress
            headline="Hvert ertu að flytja?"
            placeholder="Heimilisfang"
            isNewAddress={true}
            setProperty={setNewProperty}
            onClickForward={() => {
              setSteps('SelectPackageForNewAddress')
            }}
          />
        )}
        {steps === 'SelectPackageForNewAddress' &&
          newProperty !== undefined && (
            <>
              <H4 as="h3">Viltu velja orkuleið?</H4>
              <p className={s.form__spacing}>
                Hér getur þú valið þá orkuleið sem hentar þér best. Viltu ákveða
                þig seinna? Ekkert mál! Þú getur valið eða breytt orkuleiðum
                þínum gegnum Mínar síður hvenær sem er.
              </p>
              <SelectPackageForOne
                setProperty={setNewProperty}
                property={newProperty}
              />
              <Check
                name="propertyDelivered"
                id="propertyDelivered"
                value="Ég hef fengið nýju eignina afhenta"
                label="Ég hef fengið nýju eignina afhenta"
                onChange={(e) => {
                  onPropertyDeliveredChange(e.currentTarget.checked)
                }}
              />
              <div className={s.buttonContainer}>
                <BackButton onClick={() => setSteps('SelectNewAddress')}>
                  Til baka
                </BackButton>
                <ArrowButton onClick={() => setSteps('SelectOldAddress')}>
                  Áfram
                </ArrowButton>
              </div>
            </>
          )}
        {steps === 'SelectOldAddress' && (
          <FindAddress
            headline="Hvert viltu fá kassana senda?"
            placeholder="Heimilisfang fyrir kassana"
            setProperty={setOldProperty}
            onClickForward={onClickSubmit}
            backButtonOnClick={() => setSteps('SelectPackageForNewAddress')}
          />
        )}
      </div>
    </div>
  )
}
